import { apiClient } from "./api";

export interface StaffMember {
  createdAt: string;
  email: string;
  groupId: string;
  id: string;
  name: string;
  updatedAt: string;
  userId: string;
  version: number;
}

export interface StaffGroup {
  id: string;
  name: string;
  permissions: string[];
  version: number;
}

export interface GroupInput {
  name: string;
  permissions: string[];
}

export interface SiteSettingsData {
  currentSeasonId?: string | null;
  defaultIconStoragePath?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  twitterUrl?: string | null;
}

export interface SiteSettingsDocument {
  data: SiteSettingsData;
  updatedAt: string;
  version: number;
}

export interface InviteInput {
  email: string;
  groupId: string;
  name: string;
}

export const staffQueryKey = ["admin", "staff"] as const;
export const groupsQueryKey = ["admin", "groups"] as const;

export const fetchStaff = async (): Promise<StaffMember[]> => {
  const { data } = await apiClient.get<{ staff?: StaffMember[] }>("/api/staff");
  return data.staff ?? [];
};

export const fetchGroups = async (): Promise<StaffGroup[]> => {
  const { data } = await apiClient.get<{ groups?: StaffGroup[] }>(
    "/api/groups"
  );
  return data.groups ?? [];
};

export const createGroup = async (input: GroupInput): Promise<StaffGroup> => {
  const { data } = await apiClient.post<{ group: StaffGroup }>(
    "/api/groups",
    input
  );
  return data.group;
};

export const updateGroup = async (
  group: StaffGroup,
  input: GroupInput
): Promise<StaffGroup> => {
  const { data } = await apiClient.patch<{ group: StaffGroup }>(
    `/api/groups/${group.id}`,
    { ...input, version: group.version }
  );
  return data.group;
};

export const deleteGroup = (group: StaffGroup) =>
  apiClient.delete(`/api/groups/${group.id}`);

export const inviteStaff = (input: InviteInput) =>
  apiClient.post("/api/staff", input);

export const changeStaffGroup = (member: StaffMember, groupId: string) =>
  apiClient.patch(`/api/staff/${member.id}`, {
    groupId,
    version: member.version,
  });

export const removeStaff = (member: StaffMember) =>
  apiClient.delete(`/api/staff/${member.id}`);

export const fetchAdminSettings =
  async (): Promise<SiteSettingsDocument | null> => {
    const { data } = await apiClient.get<{ settings?: SiteSettingsDocument }>(
      "/api/settings"
    );
    return data.settings ?? null;
  };

export const saveSiteSettings = (
  values: SiteSettingsData & { version: number }
) => apiClient.put("/api/settings", values);

export const uploadDefaultIcon = (file: File) =>
  apiClient.post("/api/uploads/icons/default", file, {
    headers: {
      "Content-Type": file.type,
      "X-File-Name": file.name,
    },
  });

export const updateDisplayName = (name: string) =>
  apiClient.post("/api/auth/update-user", { name });

const externalUrl = /^https?:\/\//u;

export const storageUrl = (storagePath: string | null | undefined) => {
  if (!storagePath) {
    return null;
  }
  if (externalUrl.test(storagePath)) {
    return storagePath;
  }
  return `${import.meta.env.VITE_ASSET_ORIGIN ?? ""}/${storagePath}`;
};

export const initialsOf = (text: string): string =>
  text
    .split(/\s+/u)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export const groupOptions = (groups: StaffGroup[]) =>
  groups.map((group) => ({ label: group.name, value: group.id }));
